if (!customElements.get('review-section')) {
  customElements.define('review-section', class ReviewSection extends HTMLElement {
    constructor() {
      super();
      // 顶部产品导航容器：用于读取实际高度，并在高亮切换时保证当前项可见。
      /** @type {HTMLElement | null} */
      this.navWrapper = this.querySelector('.review-section__nav-wrapper');
      // 顶部导航按钮集合：点击后平滑滚动到对应商品评价区。
      /** @type {NodeListOf<HTMLElement>} */
      this.navItems = this.querySelectorAll('.review-section__nav-item');
      // 每个商品对应的评价内容区：用于 IntersectionObserver 监听可视区域。
      /** @type {NodeListOf<HTMLElement>} */
      this.productSections = this.querySelectorAll('.review-section__product');
      this.currentNavHeight = 0;
      this.currentHeaderOffset = 0;
      // 当滚动到不同商品区时，用它来同步更新顶部导航的高亮状态。
      /** @type {IntersectionObserver | null} */
      this.observer = null;
      /** @type {HTMLElement | null} */
      this.videoModal = null;
      /** @type {HTMLElement | null} */
      this.videoModalContent = null;
      // 记录打开视频弹窗前的触发按钮，关闭后把焦点还原回去，便于键盘操作。
      /** @type {HTMLElement | null} */
      this.lastActiveElement = null;
      
      this.handleResize = this.handleResize.bind(this);
      this.onResize = this.debounce(this.handleResize, 150);
      this.onComponentClick = this.onComponentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onHeaderChange = this.onHeaderChange.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.userInteracting = false;
      this.scrollEndTimer = undefined;
      this.pendingTabActivationTimer = undefined;
      this.pendingTabActivation = null;
      this.initialized = false;
    }

    connectedCallback() {
      // 自定义元素可能因为 DOM 重新挂载而多次进入，这里只初始化一次核心逻辑。
      if (!this.initialized) {
        this.init();
        this.initialized = true;
      }

      // 绑定所有运行期事件：窗口尺寸变化、滚动、组件内部点击、键盘关闭弹窗等。
      window.addEventListener('resize', this.onResize);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.addEventListener('click', this.onComponentClick);
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('header:scrolled', this.onHeaderChange);
      // 触摸滚动时不要强制把高亮按钮滚回可视区域，避免和用户手势打架。
      this.addEventListener('touchstart', () => { this.userInteracting = true; }, { passive: true });
      this.addEventListener('touchmove', () => { this.userInteracting = true; }, { passive: true });
      this.addEventListener('touchend', () => { this.userInteracting = false; }, { passive: true });
    }

    disconnectedCallback() {
      // 组件卸载时清理监听，避免重复绑定和潜在内存泄漏。
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('scroll', this.onScroll);
      this.removeEventListener('click', this.onComponentClick);
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('header:scrolled', this.onHeaderChange);
      if (this.observer) {
        this.observer.disconnect();
      }
      if (this.pendingTabActivationTimer != null) window.clearTimeout(this.pendingTabActivationTimer);
      if (this.scrollEndTimer != null) window.clearTimeout(this.scrollEndTimer);
      this.initialized = false;
    }

    init() {
      // 首次初始化时，先同步 CSS 变量和导航高度，再挂载交互能力。
      this.currentHeaderOffset = this.updateHeaderOffset();
      this.style.setProperty('--lyf-header-offset', `${this.currentHeaderOffset}px`);
      this.currentNavHeight = this.updateNavHeight();
      this.setupSmoothScroll();
      this.setupVideoModal();
      this.setupIntersectionObserver();
    }

    updateHeaderOffset() {
      // 主题在不同状态下可能暴露 sticky/header 两套高度变量，这里优先取 sticky 高度。
      const root = document.documentElement;
      const styles = window.getComputedStyle(root);
      const sticky = styles.getPropertyValue('--sticky-header-height').trim();
      const header = styles.getPropertyValue('--header-height').trim();
      const candidate = sticky || header;
      const value = parseFloat(candidate);
      return Number.isFinite(value) ? Math.round(value) : 0;
    }

    onHeaderChange() {
      // Header 吸顶高度变化后，重新计算 observer 的偏移，保证高亮判断仍然准确。
      const next = this.updateHeaderOffset();
      if (next !== this.currentHeaderOffset) {
        this.currentHeaderOffset = next;
        this.style.setProperty('--lyf-header-offset', `${this.currentHeaderOffset}px`);
        this.setupIntersectionObserver();
      }
    }

    onScroll() {
      // 短暂延后重置交互状态，用于区分“用户正在手动滑动”和“脚本触发滚动”。
      if (this.scrollEndTimer != null) window.clearTimeout(this.scrollEndTimer);
      this.scrollEndTimer = window.setTimeout(() => {
        this.userInteracting = false;
      }, 120);
    }

    setupIntersectionObserver() {
      if (this.observer) {
        this.observer.disconnect();
      }

      // 顶部 rootMargin 需要扣掉 header 和导航高度，让“进入视口”的判断更贴近真实可见区域。
      const options = {
        rootMargin: `-${this.currentHeaderOffset + this.currentNavHeight + 20}px 0px -60% 0px`,
        threshold: 0
      };

      const observer = new IntersectionObserver((/** @type {IntersectionObserverEntry[]} */ entries) => {
        /** @type {string | null} */
        let currentId = null;
        
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // 多个区块同时命中时，后出现的命中项会成为当前激活项，符合页面向下阅读的直觉。
            currentId = entry.target.getAttribute('id');
          }
        });

        if (currentId) {
          this.navItems.forEach(item => {
            if (item.getAttribute('data-target') === currentId) {
              if (item.getAttribute('aria-current') !== 'true') {
                this.navItems.forEach((nav) => {
                  nav.classList.toggle('button--primary', nav === item);
                  nav.classList.toggle('button--secondary', nav !== item);
                  if (nav === item) {
                    nav.setAttribute('aria-current', 'true');
                  } else {
                    nav.removeAttribute('aria-current');
                  }
                });
                const wrapperTop = this.navWrapper?.getBoundingClientRect().top;
                if (typeof wrapperTop === 'number' && Math.round(wrapperTop) <= this.currentHeaderOffset + 2) {
                  if (!this.userInteracting) {
                    // 只有在导航已经贴顶且用户未主动拖动时，才自动横向滚动导航，避免体验突兀。
                    item.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'center' });
                  }
                }
              }
            }
          });
        }
      }, options);

      this.observer = observer;
      this.productSections.forEach(section => observer.observe(section));
    }

    setupVideoModal() {
      // 缓存弹窗节点，避免每次打开都重复 query。
      this.videoModal = this.querySelector('.review-video-modal');
      this.videoModalContent = this.querySelector('.review-video-modal__content');
    }

    /** @param {string | undefined} videoUrl */
    isYouTubeUrl(videoUrl) {
      if (!videoUrl || videoUrl.trim() === '') return false;

      try {
        const url = new URL(videoUrl, window.location.origin);
        const hostname = url.hostname.toLowerCase();
        return hostname.includes('youtube') || hostname === 'youtu.be';
      } catch (error) {
        return /(?:youtube|youtu\.be)/i.test(videoUrl);
      }
    }

    /** @param {string | undefined} value */
    extractYouTubeVideoId(value) {
      if (!value || value.trim() === '') return '';

      const trimmedValue = value.trim();
      const idPattern = /^[A-Za-z0-9_-]{11}$/;

      if (idPattern.test(trimmedValue)) {
        return trimmedValue;
      }

      try {
        const url = new URL(trimmedValue, window.location.origin);
        const hostname = url.hostname.toLowerCase().replace(/^www\./, '').replace(/^m\./, '');
        const pathname = url.pathname.replace(/\/+$/, '');
        const segments = pathname.split('/').filter(Boolean);

        const searchId = url.searchParams.get('v') || url.searchParams.get('vi');
        if (searchId && idPattern.test(searchId)) {
          return searchId;
        }

        if (hostname === 'youtu.be' && segments[0] && idPattern.test(segments[0])) {
          return segments[0];
        }

        if (hostname.includes('youtube')) {
          for (let index = 0; index < segments.length; index += 1) {
            const segment = segments[index];
            const nextSegment = segments[index + 1];

            if (['embed', 'shorts', 'live', 'v', 'e'].includes(segment) && nextSegment && idPattern.test(nextSegment)) {
              return nextSegment;
            }

            if (idPattern.test(segment)) {
              return segment;
            }
          }
        }
      } catch (error) {
        const matchedId = trimmedValue.match(/(?:v=|\/embed\/|\/shorts\/|\/live\/|youtu\.be\/)([A-Za-z0-9_-]{11})/i);
        return matchedId ? matchedId[1] : '';
      }

      return '';
    }

    /** @param {HTMLElement} btn */
    openVideoModal(btn) {
      const videoType = btn.dataset.videoType;
      const videoUrl = btn.dataset.videoUrl;
      const videoId = btn.dataset.videoId;

      if (!videoUrl || videoUrl.trim() === '') return;
      if (!this.videoModalContent || !this.videoModal) return;

      this.lastActiveElement = btn;

      // 每次打开前先清空旧内容，避免连续切换视频时残留上一次的播放器。
      this.videoModalContent.innerHTML = '';

      const isYouTube = videoType === 'youtube' || this.isYouTubeUrl(videoUrl);
      const resolvedVideoId = this.extractYouTubeVideoId(videoId) || this.extractYouTubeVideoId(videoUrl);

      if (isYouTube && resolvedVideoId) {
        const iframe = document.createElement('iframe');
        iframe.src = `https://www.youtube.com/embed/${encodeURIComponent(resolvedVideoId)}?autoplay=1&rel=0&showinfo=0`;
        iframe.setAttribute('frameborder', '0');
        iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture');
        iframe.setAttribute('allowfullscreen', 'true');
        this.videoModalContent.appendChild(iframe);
      } else {
        const video = document.createElement('video');
        video.controls = true;
        video.autoplay = true;
        video.preload = 'metadata';
        
        const source = document.createElement('source');
        source.src = encodeURI(videoUrl);
        source.type = 'video/mp4';
        
        video.appendChild(source);
        const fallbackText = this.getAttribute('data-video-unsupported-text');
        if (fallbackText && fallbackText.trim() !== '') {
          video.appendChild(document.createTextNode(fallbackText));
        }
        this.videoModalContent.appendChild(video);
      }

      this.videoModal.removeAttribute('hidden');
      // 打开弹窗时锁定页面背景滚动，避免穿透滚动。
      document.body.style.overflow = 'hidden';
      
      const closeBtn = this.videoModal?.querySelector('.review-video-modal__close');
      if (closeBtn instanceof HTMLElement) closeBtn.focus();
    }

    closeVideoModal() {
      if (this.videoModal) {
        this.videoModal.setAttribute('hidden', '');
      }
      if (this.videoModalContent) {
        this.videoModalContent.innerHTML = ''; 
      }
      document.body.style.overflow = '';
      // 关闭后把焦点送回触发按钮，保证键盘和辅助技术的操作连续性。
      if (this.lastActiveElement instanceof HTMLElement) {
        this.lastActiveElement.focus();
      }
    }

    updateNavHeight() {
      if (this.navWrapper) {
        // 导航高度会影响滚动定位和 observer 的 rootMargin，所以每次都取实时值。
        const height = Math.round(this.navWrapper.getBoundingClientRect().height);
        this.style.setProperty('--nav-height', `${height}px`);
        return height;
      }
      return 0;
    }

    handleResize() {
      // 尺寸变化后统一刷新 header 偏移、导航高度和 observer 配置。
      this.currentHeaderOffset = this.updateHeaderOffset();
      this.style.setProperty('--lyf-header-offset', `${this.currentHeaderOffset}px`);
      this.currentNavHeight = this.updateNavHeight();
      this.setupIntersectionObserver();
    }

    /** @param {(...args: any[]) => void} func @param {number} wait */
    debounce(func, wait) {
      /** @type {number | undefined} */
      let timeout;
      /** @param {...any} args */
      return function executedFunction(...args) {
        const later = () => {
          if (timeout != null) window.clearTimeout(timeout);
          func(...args);
        };
        // 频繁触发时只保留最后一次执行，减少 resize 带来的重复计算。
        if (timeout != null) window.clearTimeout(timeout);
        timeout = window.setTimeout(later, wait);
      };
    }

    setupSmoothScroll() {
      this.navItems.forEach(item => {
        item.addEventListener('click', (/** @type {MouseEvent} */ e) => {
          e.preventDefault();
          this.currentHeaderOffset = this.updateHeaderOffset();
          this.style.setProperty('--lyf-header-offset', `${this.currentHeaderOffset}px`);
          this.currentNavHeight = this.updateNavHeight();
          this.navItems.forEach((nav) => {
            nav.classList.toggle('button--primary', nav === item);
            nav.classList.toggle('button--secondary', nav !== item);
            if (nav === item) {
              nav.setAttribute('aria-current', 'true');
            } else {
              nav.removeAttribute('aria-current');
            }
          });
          item.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'center' });
          const targetId = item.getAttribute('data-target');
          if (!targetId) return;
          const targetSection = document.getElementById(targetId);
          if (targetSection) {
            const stickyOffset = this.currentHeaderOffset + this.currentNavHeight + 20;
            const targetTop = Math.max(
              0,
              Math.round(window.scrollY + targetSection.getBoundingClientRect().top - stickyOffset)
            );
            const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true;

            window.scrollTo({
              top: targetTop,
              behavior: prefersReducedMotion ? 'auto' : 'smooth'
            });
          }
        });
      });
    }

    /** @param {HTMLElement} tab */
    requestTabActivation(tab) {
      this.pendingTabActivation = tab;
      if (this.pendingTabActivationTimer != null) window.clearTimeout(this.pendingTabActivationTimer);
      this.pendingTabActivationTimer = window.setTimeout(() => {
        const nextTab = this.pendingTabActivation;
        this.pendingTabActivation = null;
        this.pendingTabActivationTimer = undefined;
        if (!(nextTab instanceof HTMLElement)) return;
        try {
          this.activateTab(nextTab);
        } catch (error) {
          const panelId = nextTab.getAttribute('aria-controls');
          const panel = panelId ? document.getElementById(panelId) : null;
          if (panel instanceof HTMLElement) {
            panel.removeAttribute('hidden');
            panel.scrollIntoView({ behavior: 'auto', block: 'start' });
          }
        }
      }, 80);
    }

    /** @param {HTMLElement} tab */
    activateTab(tab) {
      const tabList = tab.closest('.review-section__tab-list');
      if (!(tabList instanceof HTMLElement)) return;

      const tabs = Array.from(tabList.querySelectorAll('.review-section__tab-btn[role="tab"]'))
        .filter((el) => el instanceof HTMLElement);

      if (tabs.length === 0) return;

      const currentTab = tabs.find((item) => item.getAttribute('aria-selected') === 'true') || null;
      if (currentTab === tab) return;

      const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true;
      const panelInDurationMs = prefersReducedMotion ? 0 : 180;
      const panelOutDurationMs = prefersReducedMotion ? 0 : 140;

      const toPanelId = tab.getAttribute('aria-controls');
      const toPanel = toPanelId ? document.getElementById(toPanelId) : null;
      const fromPanelId = currentTab ? currentTab.getAttribute('aria-controls') : null;
      const fromPanel = fromPanelId ? document.getElementById(fromPanelId) : null;

      tabs.forEach((item) => {
        const isActive = item === tab;
        item.classList.toggle('button--primary', isActive);
        item.classList.toggle('button--secondary', !isActive);
        item.setAttribute('aria-selected', isActive ? 'true' : 'false');
        item.setAttribute('tabindex', isActive ? '0' : '-1');

        const panelId = item.getAttribute('aria-controls');
        if (!panelId) return;
        const panel = document.getElementById(panelId);
        if (!(panel instanceof HTMLElement)) return;
        if (!isActive && panel !== toPanel) {
          panel.setAttribute('hidden', '');
        }
      });

      const canAnimatePanel = (panel) => panel instanceof HTMLElement && typeof panel.animate === 'function' && typeof panel.getAnimations === 'function';

      if (toPanel instanceof HTMLElement) {
        if (canAnimatePanel(toPanel)) toPanel.getAnimations().forEach((animation) => animation.cancel());
        toPanel.removeAttribute('hidden');

        if (panelInDurationMs > 0 && canAnimatePanel(toPanel)) {
          toPanel.style.opacity = '0';
          toPanel.style.transform = 'translateY(0.75rem)';
          const animation = toPanel.animate(
            { opacity: [0, 1], transform: ['translateY(0.75rem)', 'translateY(0)'] },
            { duration: panelInDurationMs, easing: 'ease-out', fill: 'forwards' }
          );
          animation.finished.finally(() => {
            if (!(toPanel instanceof HTMLElement)) return;
            toPanel.style.opacity = '';
            toPanel.style.transform = '';
          });
        } else {
          toPanel.style.opacity = '';
          toPanel.style.transform = '';
        }
      }

      if (fromPanel instanceof HTMLElement && fromPanel !== toPanel) {
        if (canAnimatePanel(fromPanel)) fromPanel.getAnimations().forEach((animation) => animation.cancel());

        if (panelOutDurationMs > 0 && canAnimatePanel(fromPanel)) {
          const animation = fromPanel.animate(
            { opacity: [1, 0], transform: ['translateY(0)', 'translateY(0.5rem)'] },
            { duration: panelOutDurationMs, easing: 'ease-in', fill: 'forwards' }
          );
          animation.finished.finally(() => {
            if (!(fromPanel instanceof HTMLElement)) return;
            fromPanel.setAttribute('hidden', '');
            fromPanel.style.opacity = '';
            fromPanel.style.transform = '';
          });
        } else {
          fromPanel.setAttribute('hidden', '');
        }
      }
    }

    /** @param {MouseEvent} event */
    onComponentClick(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const tab = target.closest('.review-section__tab-btn[role="tab"]');
      if (tab instanceof HTMLElement && this.contains(tab)) {
        this.requestTabActivation(tab);
        return;
      }

      const openBtn = target.closest('.js-open-video-modal');
      if (openBtn instanceof HTMLElement) {
        this.openVideoModal(openBtn);
        return;
      }

      const closeBtn = target.closest('.js-close-video-modal');
      if (closeBtn) {
        this.closeVideoModal();
      }
    }

    /** @param {KeyboardEvent} e */
    onKeydown(e) {
      if (!this.videoModal || this.videoModal.hasAttribute('hidden')) {
        const target = e.target;
        if (!(target instanceof Element)) return;

        const tab = target.closest('.review-section__tab-btn[role="tab"]');
        if (!(tab instanceof HTMLElement) || !this.contains(tab)) return;

        const tabList = tab.closest('.review-section__tab-list');
        if (!(tabList instanceof HTMLElement)) return;

        const tabs = Array.from(tabList.querySelectorAll('.review-section__tab-btn[role="tab"]'))
          .filter((el) => el instanceof HTMLElement);

        if (tabs.length === 0) return;

        const currentIndex = tabs.indexOf(tab);
        if (currentIndex < 0) return;

        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.requestTabActivation(tab);
          return;
        }

        let nextIndex = currentIndex;

        if (e.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
        if (e.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        if (e.key === 'Home') nextIndex = 0;
        if (e.key === 'End') nextIndex = tabs.length - 1;

        if (nextIndex !== currentIndex) {
          e.preventDefault();
          const nextTab = tabs[nextIndex];
          nextTab.focus();
          this.requestTabActivation(nextTab);
        }
        return;
      }

      if (e.key === 'Escape') {
        this.closeVideoModal();
        return;
      }

      if (e.key !== 'Tab') return;

      // 弹窗开启时把 Tab 焦点限制在弹窗内部，避免焦点“跑出”遮罩层。
      const focusables = Array.from(this.videoModal.querySelectorAll('button, [href], input, select, textarea, iframe, video, [tabindex]:not([tabindex="-1"])'))
        .filter((el) => el instanceof HTMLElement && !el.hasAttribute('disabled') && !el.hasAttribute('hidden'));

      if (focusables.length === 0) return;

      const first = /** @type {HTMLElement} */ (focusables[0]);
      const last = /** @type {HTMLElement} */ (focusables[focusables.length - 1]);
      const active = document.activeElement;

      if (e.shiftKey) {
        if (active === first || active === this.videoModal) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

  });
}
